import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";

import Container from "../../../../components/Container";
import Loader from "../../../../components/Loader";
import AlertMsg from "../../../../components/AlertMsg";
import AppDialog from "../../../../components/AppDialog";
import RestaurantHeader from "../../../../components/RestaurantHeader";

import { AttributeList, AttributeFormDialog } from "../components";
import {
  useGetAttributes,
  useCreateAttribute,
  useUpdateAttribute,
  useDeleteAttribute,
} from "../services";
import { TAttribute, TCreateAttributeRequest } from "../schemas";

const AttributesPage = () => {
  const { rest_id: restaurantId } = useParams<{ rest_id: string }>();
  const navigate = useNavigate();

  const [formDialogOpen, setFormDialogOpen] = useState(false);
  const [selectedAttribute, setSelectedAttribute] = useState<TAttribute | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [attributeToDelete, setAttributeToDelete] = useState<TAttribute | null>(null);
  const [msgAlert, setMsgAlert] = useState({ open: false, message: "", msgType: "error" });

  const { data: attributes, isLoading, refetch } = useGetAttributes(restaurantId || "");
  const createMutation = useCreateAttribute(restaurantId || "");
  const updateMutation = useUpdateAttribute();
  const deleteMutation = useDeleteAttribute();

  const handleAddClick = () => {
    setSelectedAttribute(null);
    setFormDialogOpen(true);
  };

  const handleEditClick = (attribute: TAttribute) => {
    setSelectedAttribute(attribute);
    setFormDialogOpen(true);
  };

  const handleDeleteClick = (attribute: TAttribute) => {
    setAttributeToDelete(attribute);
    setDeleteDialogOpen(true);
  };

  const handleFormSubmit = async (data: TCreateAttributeRequest) => {
    try {
      if (selectedAttribute) {
        await updateMutation.mutateAsync({ attributeId: selectedAttribute._id, data });
        setMsgAlert({ open: true, message: "Attribute updated successfully", msgType: "success" });
      } else {
        await createMutation.mutateAsync(data);
        setMsgAlert({ open: true, message: "Attribute created successfully", msgType: "success" });
      }
      setFormDialogOpen(false);
      refetch();
    } catch (error) {
      setMsgAlert({
        open: true,
        message: selectedAttribute ? "Failed to update attribute" : "Failed to create attribute",
        msgType: "error",
      });
    }
  };

  const handleDeleteConfirm = async () => {
    if (!attributeToDelete) return;

    try {
      await deleteMutation.mutateAsync(attributeToDelete._id);
      setMsgAlert({ open: true, message: "Attribute deleted successfully", msgType: "success" });
      setDeleteDialogOpen(false);
      setAttributeToDelete(null);
      refetch();
    } catch (error) {
      setMsgAlert({ open: true, message: "Failed to delete attribute", msgType: "error" });
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setAttributeToDelete(null);
  };

  return (
    <Container
      page="restaurants"
      searchVal=""
      onSearch={() => null}
      searchLabel=""
    >
      {isLoading ? (
        <Loader height={{ height: window.innerHeight - 150 }} />
      ) : (
        <Grid container item xs={12}>
          <Grid item xs={12} mb={2}>
            <RestaurantHeader
              title="Menu Attributes"
              onBackBtnClick={() => navigate(`/restaurant/${restaurantId}/manage_menu`)}
            />
          </Grid>
          <Grid item xs={12} sx={styles.mainView} px={3} pt={2}>
            <Box sx={styles.headerActions}>
              <Button
                onClick={handleAddClick}
                sx={styles.addBtn}
              >
                + Add Attribute
              </Button>
            </Box>

            <Grid item xs={12} mt={2}>
              <AttributeList
                attributes={attributes || []}
                onEdit={handleEditClick}
                onDelete={handleDeleteClick}
              />
            </Grid>
          </Grid>
        </Grid>
      )}

      <AttributeFormDialog
        open={formDialogOpen}
        onClose={() => setFormDialogOpen(false)}
        onSubmit={handleFormSubmit}
        editAttribute={selectedAttribute}
        isLoading={createMutation.isPending || updateMutation.isPending}
      />

      <AppDialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        title={
          <Box>
            <Typography variant="h2" sx={{ fontSize: "1.5rem", color: "#fff" }}>
              Delete Attribute?
            </Typography>
          </Box>
        }
        content={
          <Box>
            Are you sure you want to delete "{attributeToDelete?.name}"? This action cannot be undone.
          </Box>
        }
        firstBtnContent="Cancel"
        secondBtnContent="Delete"
        firstBtnClickHandler={handleDeleteCancel}
        secondBtnClickHandler={handleDeleteConfirm}
        options={{ keepMounted: false, btnLoading: deleteMutation.isPending }}
      />

      <AlertMsg
        msgAlert={msgAlert}
        onCloseAlertMsg={() => setMsgAlert({ ...msgAlert, open: false })}
      />
    </Container>
  );
};

export default AttributesPage;

const styles = {
  mainView: {
    backgroundColor: "#FFF",
    borderRadius: "8px",
    minHeight: "calc(100vh - 200px)",
  },
  headerActions: {
    display: "flex",
    justifyContent: "flex-end",
    marginBottom: "16px",
  },
  addBtn: {
    backgroundColor: "#000000",
    color: "#ffffff",
    fontSize: 14,
    borderRadius: "8px",
    textTransform: "capitalize" as const,
    paddingLeft: "1.5rem",
    paddingRight: "1.5rem",
    paddingTop: "8px",
    paddingBottom: "8px",
    "&:hover": {
      backgroundColor: "#333333",
    },
  },
};
